// Match Calculation Service
// Calculates real match percentages based on user profile vs scholarship eligibility

import { UserProfile } from '@/types/profile';
 import { EligibilityCriteria, MatchResult, CriteriaMatch, GradeLevel, gradeLevelLabels, UncheckableCriterion } from '@/types/eligibility';
import { Scholarship } from '@/types';
import { scholarshipEligibility } from '@/data/scholarshipEligibility';

 // US state name ↔ abbreviation mapping for residency matching
 // Eligibility data may use abbreviations ('MD') while profiles store full names ('Maryland')
 const STATE_ABBREVIATIONS: Record<string, string> = {
   'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR',
   'california': 'CA', 'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE',
   'florida': 'FL', 'georgia': 'GA', 'hawaii': 'HI', 'idaho': 'ID',
   'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA', 'kansas': 'KS',
   'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
   'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS',
   'missouri': 'MO', 'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV',
   'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
   'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH', 'oklahoma': 'OK',
   'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
   'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT',
   'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV',
   'wisconsin': 'WI', 'wyoming': 'WY', 'district of columbia': 'DC',
   'puerto rico': 'PR', 'guam': 'GU', 'us virgin islands': 'VI',
   'american samoa': 'AS', 'northern mariana islands': 'MP',
 };

 // Reverse lookup: abbreviation → full name (built once at module load)
 const ABBREVIATION_TO_STATE: Record<string, string> = Object.fromEntries(
   Object.entries(STATE_ABBREVIATIONS).map(([name, abbr]) => [abbr, name])
 );

 /**
  * Normalize a state value to a consistent lowercase full name for comparison.
  * Accepts both full names ("Maryland") and abbreviations ("MD").
  */
 function normalizeState(value: string): string {
   const lower = value.trim().toLowerCase();
   // If it's a known abbreviation, convert to full name
   if (ABBREVIATION_TO_STATE[value.trim().toUpperCase()]) {
     return ABBREVIATION_TO_STATE[value.trim().toUpperCase()].toLowerCase();
   }
   return lower;
 }

 // All valid specific grade levels that can appear in eligibility data
 const SPECIFIC_GRADE_LEVELS: GradeLevel[] = [
   'middle-school-6th', 'middle-school-7th', 'middle-school-8th',
   'high-school-freshman', 'high-school-sophomore', 'high-school-junior', 'high-school-senior',
   'hs-graduate',
   'college-freshman', 'college-sophomore', 'college-junior', 'college-senior',
   'graduate',
 ];

 // Helper to convert profile grade level to eligibility format
 // Handles both specific format ('high-school-freshman' from ProfileVault/DB)
 // and legacy short format ('freshman' from old profiles or defaults)
 function mapProfileGradeToEligibility(profileGrade: string): GradeLevel[] {
   // If the profile already uses a specific format, return it directly
   if (SPECIFIC_GRADE_LEVELS.includes(profileGrade as GradeLevel)) {
     return [profileGrade as GradeLevel];
   }

   // Legacy short-format fallback — map ambiguously to both HS and college
   // This only applies to old profiles that haven't been updated via ProfileVault
   const legacyMappings: Record<string, GradeLevel[]> = {
     'middle-school': ['middle-school-6th', 'middle-school-7th', 'middle-school-8th'],
     'freshman': ['high-school-freshman', 'college-freshman'],
     'sophomore': ['high-school-sophomore', 'college-sophomore'],
     'junior': ['high-school-junior', 'college-junior'],
     'senior': ['high-school-senior', 'college-senior'],
     'hs-graduate': ['high-school-senior', 'hs-graduate', 'college-freshman'],
     'graduate': ['graduate'],
   };
   return legacyMappings[profileGrade] || [];
 }
 
// Weight multipliers for different criteria
const WEIGHTS = {
  gpa: 15,
  major: 20,
  ethnicity: 15,
  gender: 10,
  state: 15,
  financialNeed: 15,
  pellGrant: 10,
  firstGen: 10,
  citizenship: 20, // High because it's often a hard requirement
  extracurriculars: 10,
  gradeLevel: 10,
};

export function calculateMatch(
  profile: UserProfile,
  eligibility: EligibilityCriteria
): MatchResult {
  const criteriaResults: CriteriaMatch[] = [];
  let totalWeight = 0;
  let matchedWeight = 0;
  let isEligible = true;

  // GPA Check — skip entirely if user hasn't set their GPA yet
  if (eligibility.minGPA !== undefined) {
    if (profile.gpa !== null && profile.gpa !== undefined) {
      const matches = profile.gpa >= eligibility.minGPA;
      criteriaResults.push({
        criterion: 'gpa',
        label: `Minimum GPA: ${eligibility.minGPA}`,
        matches,
        required: true,
        weight: WEIGHTS.gpa,
        userValue: profile.gpa.toFixed(2),
        requiredValue: eligibility.minGPA.toString(),
      });
      totalWeight += WEIGHTS.gpa;
      if (matches) matchedWeight += WEIGHTS.gpa;
      if (!matches) isEligible = false;
    }
    // When GPA is null (not yet set), we skip this criterion entirely
    // so the scholarship isn't falsely marked "Not Eligible"
  }

  // Major Check
  if (eligibility.requiredMajors && eligibility.requiredMajors.length > 0) {
    const matches = eligibility.requiredMajors.includes(profile.majorCategory);
    criteriaResults.push({
      criterion: 'major',
      label: `Field of Study: ${eligibility.requiredMajors.join(', ')}`,
      matches,
      required: true,
      weight: WEIGHTS.major,
      userValue: profile.majorCategory,
      requiredValue: eligibility.requiredMajors.join(', '),
    });
    totalWeight += WEIGHTS.major;
    if (matches) matchedWeight += WEIGHTS.major;
    if (!matches) isEligible = false;
  }

  // Ethnicity Check (for diversity scholarships)
  if (eligibility.targetEthnicities && eligibility.targetEthnicities.length > 0) {
    const matches = profile.ethnicity.some(e => eligibility.targetEthnicities!.includes(e));
    criteriaResults.push({
      criterion: 'ethnicity',
      label: 'Diversity/Heritage Requirement',
      matches,
      required: true,
      weight: WEIGHTS.ethnicity,
      userValue: profile.ethnicity.join(', '),
      requiredValue: eligibility.targetEthnicities.join(', '),
    });
    totalWeight += WEIGHTS.ethnicity;
    if (matches) matchedWeight += WEIGHTS.ethnicity;
    if (!matches) isEligible = false;
  }

  // Gender Check (case-insensitive + normalize underscores to hyphens)
  if (eligibility.targetGenders && eligibility.targetGenders.length > 0) {
    const normalizedProfile = profile.gender.toLowerCase().replace(/_/g, '-');
    const normalizedTargets = (eligibility.targetGenders as string[]).map(g => g.toLowerCase().replace(/_/g, '-'));
    const matches = normalizedTargets.includes(normalizedProfile);
    criteriaResults.push({
      criterion: 'gender',
      label: `Gender: ${eligibility.targetGenders.join(' or ')}`,
      matches,
      required: true,
      weight: WEIGHTS.gender,
      userValue: profile.gender,
      requiredValue: eligibility.targetGenders.join(', '),
    });
    totalWeight += WEIGHTS.gender;
    if (matches) matchedWeight += WEIGHTS.gender;
    if (!matches) isEligible = false;
  }

  // State/Location Check (normalize both sides — profile may use full names, eligibility may use abbreviations)
  if (eligibility.targetStates && eligibility.targetStates.length > 0) {
    const normalizedProfileState = normalizeState(profile.state);
    const matches = eligibility.targetStates.some(s => normalizeState(s) === normalizedProfileState);
    criteriaResults.push({
      criterion: 'state',
      label: `Location: ${eligibility.targetStates.length > 3 ? 'Regional' : eligibility.targetStates.join(', ')}`,
      matches,
      required: true,
      weight: WEIGHTS.state,
      userValue: profile.state,
      requiredValue: eligibility.targetStates.join(', '),
    });
    totalWeight += WEIGHTS.state;
    if (matches) matchedWeight += WEIGHTS.state;
    if (!matches) isEligible = false;
  }

  // Financial Need Check
  if (eligibility.requiresFinancialNeed) {
    const matches = profile.financialNeed === 'high' || profile.financialNeed === 'moderate';
    criteriaResults.push({
      criterion: 'financialNeed',
      label: 'Demonstrates Financial Need',
      matches,
      required: true,
      weight: WEIGHTS.financialNeed,
      userValue: profile.financialNeed,
      requiredValue: 'high or moderate',
    });
    totalWeight += WEIGHTS.financialNeed;
    if (matches) matchedWeight += WEIGHTS.financialNeed;
    if (!matches) isEligible = false;
  }

  // Pell Grant Check
  if (eligibility.requiresPellGrant) {
    const matches = profile.pellGrantEligible;
    criteriaResults.push({
      criterion: 'pellGrant',
      label: 'Pell Grant Eligible',
      matches,
      required: true,
      weight: WEIGHTS.pellGrant,
      userValue: profile.pellGrantEligible ? 'Yes' : 'No',
      requiredValue: 'Yes',
    });
    totalWeight += WEIGHTS.pellGrant;
    if (matches) matchedWeight += WEIGHTS.pellGrant;
    if (!matches) isEligible = false;
  }

  // First Generation Check
  if (eligibility.requiresFirstGen) {
    const matches = profile.firstGeneration;
    criteriaResults.push({
      criterion: 'firstGen',
      label: 'First-Generation College Student',
      matches,
      required: true,
      weight: WEIGHTS.firstGen,
      userValue: profile.firstGeneration ? 'Yes' : 'No',
      requiredValue: 'Yes',
    });
    totalWeight += WEIGHTS.firstGen;
    if (matches) matchedWeight += WEIGHTS.firstGen;
    if (!matches) isEligible = false;
  }

  // Citizenship Check (case-insensitive + normalize underscores to hyphens)
  // citizenshipRequired can be a string OR an array — always normalize to array first
  if (eligibility.citizenshipRequired && eligibility.citizenshipRequired.length > 0) {
    const citizenshipArr = Array.isArray(eligibility.citizenshipRequired)
      ? eligibility.citizenshipRequired
      : [eligibility.citizenshipRequired];
    const normalizedProfile = profile.citizenship.toLowerCase().replace(/_/g, '-');
    const normalizedTargets = citizenshipArr.map(c => c.toLowerCase().replace(/_/g, '-'));
    const matches = normalizedTargets.includes(normalizedProfile);
    criteriaResults.push({
      criterion: 'citizenship',
      label: 'Citizenship/Residency',
      matches,
      required: true,
      weight: WEIGHTS.citizenship,
      userValue: profile.citizenship,
      requiredValue: citizenshipArr.join(', '),
    });
    totalWeight += WEIGHTS.citizenship;
    if (matches) matchedWeight += WEIGHTS.citizenship;
    if (!matches) isEligible = false;
  }

  // Extracurriculars Check (bonus, not required)
  if (eligibility.requiredActivities && eligibility.requiredActivities.length > 0) {
    const matchingActivities = profile.extracurriculars.filter(e => 
      eligibility.requiredActivities!.includes(e)
    );
    const matches = matchingActivities.length > 0;
    criteriaResults.push({
      criterion: 'extracurriculars',
      label: `Activities: ${eligibility.requiredActivities.join(', ')}`,
      matches,
      required: false, // Usually preferred, not required
      weight: WEIGHTS.extracurriculars,
      userValue: profile.extracurriculars.join(', '),
      requiredValue: eligibility.requiredActivities.join(', '),
    });
    totalWeight += WEIGHTS.extracurriculars;
    if (matches) matchedWeight += WEIGHTS.extracurriculars;
  }

  // Grade Level Check
  if (eligibility.targetGradeLevels && eligibility.targetGradeLevels.length > 0) {
     const profileGradeLevels = mapProfileGradeToEligibility(profile.gradeLevel);
     const matches = eligibility.targetGradeLevels.some(level => profileGradeLevels.includes(level));
     const displayLabels = eligibility.targetGradeLevels.map(l => gradeLevelLabels[l] || l).join(', ');
    criteriaResults.push({
      criterion: 'gradeLevel',
       label: `Grade Level: ${displayLabels}`,
      matches,
      required: true,
      weight: WEIGHTS.gradeLevel,
      userValue: profile.gradeLevel,
       requiredValue: displayLabels,
    });
    totalWeight += WEIGHTS.gradeLevel;
    if (matches) matchedWeight += WEIGHTS.gradeLevel;
    if (!matches) isEligible = false;
  }

  // Collect uncheckable criteria — hard requirements we can't verify against the profile
  const uncheckableCriteria: UncheckableCriterion[] = [];

  if (eligibility.requiresMembership) {
    const org = typeof eligibility.requiresMembership === 'string' ? eligibility.requiresMembership : 'an organization';
    uncheckableCriteria.push({
      label: `${org} Membership`,
      description: `Must be a member of ${org}`,
    });
  }
  if (eligibility.requiresDisability) {
    uncheckableCriteria.push({
      label: 'Disability Documentation',
      description: 'Must have a documented disability',
    });
  }
  if (eligibility.requiresMilitaryConnection) {
    uncheckableCriteria.push({
      label: 'Military Connection',
      description: 'Must be a veteran, active duty, or military family',
    });
  }
  if (eligibility.requiresFosterCare) {
    uncheckableCriteria.push({
      label: 'Foster Care Background',
      description: 'Must be a current or former foster youth',
    });
  }
  if (eligibility.requiresEmployment) {
    const employer = typeof eligibility.requiresEmployment === 'string' ? eligibility.requiresEmployment : 'a specific employer';
    uncheckableCriteria.push({
      label: `${employer} Employee`,
      description: `Must be employed at ${employer}`,
    });
  }
  if (eligibility.nominationRequired) {
    uncheckableCriteria.push({
      label: 'Nomination Required',
      description: 'Must be nominated by school or institution',
    });
  }

  const confidence = uncheckableCriteria.length > 0 ? 'partial' : 'full';

  // Calculate overall match percentage
  // If no criteria could be checked, show 50% (neutral) rather than 100%
  // to avoid misleading users — we simply don't have enough data to confirm eligibility
  const baseMatch = totalWeight > 0
    ? Math.round((matchedWeight / totalWeight) * 100)
    : 50;

  // Cap at 95% when uncheckable criteria exist (membership, disability, etc.)
  // so users don't see a misleading "100%" when they haven't verified everything
  const overallMatch = uncheckableCriteria.length > 0 ? Math.min(baseMatch, 95) : baseMatch;

  return {
    overallMatch: isEligible ? overallMatch : Math.min(overallMatch, 40), // Cap at 40% if ineligible
    criteriaResults,
    eligible: isEligible,
    uncheckableCriteria,
    confidence,
  };
}

// Convert legacy scholarship data to eligibility criteria
export function inferEligibilityFromSkills(scholarship: Scholarship): EligibilityCriteria {
  const eligibility: EligibilityCriteria = {};
  
  // Parse skills for eligibility hints
  for (const skill of scholarship.skills) {
    const name = skill.name.toLowerCase();
    const desc = skill.description.toLowerCase();
    
    // GPA
    if (name.includes('gpa') || desc.includes('gpa')) {
      const gpaMatch = desc.match(/(\d\.\d)/);
      if (gpaMatch) {
        eligibility.minGPA = parseFloat(gpaMatch[1]);
      }
    }
    
    // Pell Grant
    if (name.includes('pell') || desc.includes('pell')) {
      eligibility.requiresPellGrant = true;
    }
    
    // First Gen
    if (name.includes('first-gen') || name.includes('first gen') || desc.includes('first generation')) {
      eligibility.requiresFirstGen = true;
    }
    
    // Financial Need
    if (name.includes('financial need') || desc.includes('financial need') || desc.includes('low-income')) {
      eligibility.requiresFinancialNeed = true;
    }
    
    // Leadership
    if (name.includes('leadership')) {
      eligibility.requiredActivities = eligibility.requiredActivities || [];
      if (!eligibility.requiredActivities.includes('leadership')) {
        eligibility.requiredActivities.push('leadership');
      }
    }
    
    // Community Service
    if (name.includes('service') || name.includes('community')) {
      eligibility.requiredActivities = eligibility.requiredActivities || [];
      if (!eligibility.requiredActivities.includes('community-service')) {
        eligibility.requiredActivities.push('community-service');
      }
    }
    
    // STEM
    if (name.includes('stem') || name.includes('engineering') || name.includes('science')) {
      eligibility.requiredMajors = eligibility.requiredMajors || [];
      if (!eligibility.requiredMajors.includes('stem')) {
        eligibility.requiredMajors.push('stem');
      }
    }
    
    // Arts
    if (name.includes('art') || name.includes('music') || name.includes('creative')) {
      eligibility.requiredMajors = eligibility.requiredMajors || [];
      if (!eligibility.requiredMajors.includes('arts')) {
        eligibility.requiredMajors.push('arts');
      }
    }
  }
  
  // Infer from category
  const category = scholarship.category.toLowerCase();
  if (category.includes('african american') || category.includes('black')) {
    eligibility.targetEthnicities = ['african-american'];
  } else if (category.includes('hispanic') || category.includes('latino')) {
    eligibility.targetEthnicities = ['hispanic-latino'];
  } else if (category.includes('asian')) {
    eligibility.targetEthnicities = ['asian'];
  } else if (category.includes('native')) {
    eligibility.targetEthnicities = ['native-american'];
  } else if (category.includes('women')) {
    eligibility.targetGenders = ['female'];
  }
  
  return eligibility;
}

// Get match for a scholarship using profile
export function getScholarshipMatch(
  profile: UserProfile,
  scholarship: Scholarship
): MatchResult {
  // 1. Use structured eligibility attached to the scholarship object (from getScholarshipById)
  // 2. Fall back to the eligibility map lookup by ID (for raw array items without .eligibility)
  // 3. Last resort: infer from skills text (legacy, least accurate)
  const eligibility =
    scholarship.eligibility ||
    scholarshipEligibility[scholarship.id] ||
    inferEligibilityFromSkills(scholarship);
  return calculateMatch(profile, eligibility);
}
